import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Linkedin, Twitter, Github, Mail } from "lucide-react";

type TeamMember = {
  id: string;
  name: string;
  role: string;
  photo_url: string | null;
  bio: string | null;
  location: string | null;
  department: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  github_url: string | null;
  email: string | null;
};

export function TeamSection() {
  const { data } = useQuery({
    queryKey: ["team_members", "public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select(
          "id, name, role, photo_url, bio, location, department, linkedin_url, twitter_url, github_url, email",
        )
        .eq("published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as TeamMember[];
    },
    staleTime: 60_000,
  });

  if (!data?.length) return null;

  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="mb-10 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">Team</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Meet the people behind the work</h2>
          <p className="mt-4 mx-auto max-w-2xl text-muted-foreground">
            Engineers, designers and strategists who care deeply about shipping great products.
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((m) => (
            <article
              key={m.id}
              className="group rounded-2xl border border-border bg-card p-6 shadow-card transition-colors hover:border-primary/40"
            >
              <div className="flex items-center gap-4">
                {m.photo_url ? (
                  <img
                    src={m.photo_url}
                    alt={m.name}
                    loading="lazy"
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-lg font-semibold text-muted-foreground">
                    {m.name.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="truncate font-semibold">{m.name}</h3>
                  <div className="text-sm text-primary">{m.role}</div>
                  {(m.department || m.location) && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {[m.department, m.location].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
              {m.bio && (
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{m.bio}</p>
              )}
              {(m.linkedin_url || m.twitter_url || m.github_url || m.email) && (
                <div className="mt-5 flex items-center gap-3 text-muted-foreground">
                  {m.linkedin_url && (
                    <a
                      href={m.linkedin_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`${m.name} on LinkedIn`}
                      className="hover:text-foreground"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                  {m.twitter_url && (
                    <a
                      href={m.twitter_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`${m.name} on X`}
                      className="hover:text-foreground"
                    >
                      <Twitter className="h-4 w-4" />
                    </a>
                  )}
                  {m.github_url && (
                    <a
                      href={m.github_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`${m.name} on GitHub`}
                      className="hover:text-foreground"
                    >
                      <Github className="h-4 w-4" />
                    </a>
                  )}
                  {m.email && (
                    <a
                      href={`mailto:${m.email}`}
                      aria-label={`Email ${m.name}`}
                      className="hover:text-foreground"
                    >
                      <Mail className="h-4 w-4" />
                    </a>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
